/*
 * This Composable is used for Marketing Messages Media Uploads.
 * It can probably be used for other Media Uploads as well.
 */

import { ref, type Ref } from 'vue';

export type UploadState = null | 'uploading' | 'processing';

export interface MediaItem {
  id?: string;
  url?: string;
  original_url?: string;
  links?: {
    self?: string;
    delete?: string;
  };
  [key: string]: any;
}

export function useMediaUpload(uploadUrl: string, collectionName: string) {
  const uploadState: Ref<UploadState> = ref(null);

  const refreshMediaUntilReady = async (file: MediaItem, maxTries = 10, delay = 500): Promise<MediaItem> => {
    let refreshed = file;
    let tries = 0;

    while ((!refreshed.url && !refreshed.original_url) && refreshed.links?.self && tries < maxTries) {
      await new Promise(res => setTimeout(res, delay));

      try {
        const response = await fetch(refreshed.links.self, {
          headers: { 'Accept': 'application/json' }
        });

        if (response.ok) {
          refreshed = await response.json();
          // Si c'est un resource enveloppé, prendre .data
          if (refreshed.data) {
            refreshed = refreshed.data;
          }
        }
      } catch (e) {
        // ignore les erreurs de fetch
      }

      tries++;
    }

    return refreshed;
  };

  const uploadFiles = async (files: FileList): Promise<MediaItem[]> => {
    uploadState.value = 'uploading';

    const filesUploaded: MediaItem[] = [];

    for (let i = 0; i < files.length; i++) {
      const formData = new FormData();
      formData.append('collection', collectionName);
      formData.append('image', files[i]);

      try {
        const response = await fetch(uploadUrl, {
          method: 'POST',
          body: formData,
          headers: {
            'Accept': 'application/json',
            'X-CSRF-TOKEN': window.Laravel.csrfToken || document.querySelector('meta[name="csrf-token"]').getAttribute('content') || ''
          }
        });

        if (!response.ok) {
          throw new Error('File upload failed');
        }

        const result = await response.json();
        filesUploaded.push(result.data);
      } catch (error) {
        console.error('Error uploading file:', error);
      }
    }

    return filesUploaded;
  };

  const processUploadedFiles = async (files: MediaItem[]): Promise<MediaItem[]> => {
    uploadState.value = 'processing';

    try {
      const updatedFiles = await Promise.all(
        files.map(file => refreshMediaUntilReady(file))
      );

      return updatedFiles;
    } finally {
      uploadState.value = null;
    }
  };

  const uploadAndProcessFiles = async (files: FileList): Promise<MediaItem[]> => {
    const uploadedFiles = await uploadFiles(files);
    const processedFiles = await processUploadedFiles(uploadedFiles);
    return processedFiles;
  };

  const setUploadingState = () => {
    uploadState.value = 'uploading';
  };

  const resetUploadState = () => {
    uploadState.value = null;
  };

  return {
    uploadState,
    refreshMediaUntilReady,
    uploadFiles,
    processUploadedFiles,
    uploadAndProcessFiles,
    setUploadingState,
    resetUploadState
  };
}
